import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { forgotPassword } from '../apiService';
import Carousel from './Carousel';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setNotification(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotification(null);

    if (!formData.email || !formData.newPassword || !formData.confirmPassword) {
      setNotification({ message: 'Please fill in all fields', type: 'error' }); return;
    }
    if (!formData.email.includes('@')) {
      setNotification({ message: 'Please enter a valid email address', type: 'error' }); return;
    }
    if (formData.newPassword.length < 6) {
      setNotification({ message: 'Password must be at least 6 characters', type: 'error' }); return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setNotification({ message: 'Passwords do not match', type: 'error' }); return;
    }

    setLoading(true);
    try {
      const response = await forgotPassword(formData.email, formData.newPassword);
      setNotification({ message: response.message || 'Password reset successfully!', type: 'success' });
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      setNotification({ message: error.message || 'Password reset failed. Please try again.', type: 'error' });
      console.error('Forgot password error:', error);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700 focus:outline-none focus:border-blue-500 focus:bg-white transition-colors duration-200 box-border";

  return (
    <div className="flex h-screen w-screen bg-white overflow-hidden">
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .notification-slide { animation: slideDown 0.3s ease-out; }
      `}</style>

      {/* ── Left: Carousel (hidden on mobile) ── */}
      <div className="hidden md:flex md:flex-1 bg-[#326BDF] items-center justify-center p-4 overflow-hidden">
        <Carousel initialSlide={2} />
      </div>

      {/* ── Right: Forgot Password Form ── */}
      <div className="flex-1 bg-[#EDEDED] flex items-start md:items-center justify-center overflow-y-auto py-6 px-4 md:px-6">
        <div className="w-full max-w-md">

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/Provinyx-logo-LightTheme.png"
              alt="Provinyx"
              className="h-10 md:h-12 w-auto"
            />
          </div>

          {/* Form Card */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 md:p-8 shadow-sm">
            <form className="flex flex-col gap-3" onSubmit={handleSubmit}>

              <input
                type="email" name="email" placeholder="Email"
                value={formData.email} onChange={handleChange}
                className={inputClass} disabled={loading}
              />
              <input
                type="password" name="newPassword" placeholder="New Password"
                value={formData.newPassword} onChange={handleChange}
                className={inputClass} disabled={loading}
              />
              <input
                type="password" name="confirmPassword" placeholder="Confirm New Password"
                value={formData.confirmPassword} onChange={handleChange}
                className={inputClass} disabled={loading}
              />

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 bg-[#326BDF] text-white border-none rounded-lg py-3 px-4 text-sm font-semibold cursor-pointer transition-colors duration-200 hover:bg-[#2859c5] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>

            {/* Notification */}
            {notification && (
              <div className={`notification-slide mt-5 p-4 rounded-lg flex items-center justify-between ${
                notification.type === 'success'
                  ? 'bg-green-50 border border-green-300 text-green-800'
                  : 'bg-red-50 border border-red-300 text-red-800'
              }`}>
                <span className="font-medium text-sm">{notification.message}</span>
                <button
                  onClick={() => setNotification(null)}
                  className="bg-transparent border-none text-2xl font-bold cursor-pointer opacity-70 ml-4 leading-none hover:opacity-100 text-inherit"
                >×</button>
              </div>
            )}
          </div>

          {/* Footer links */}
          <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
            <span>
              Remember your password?{' '}
              <span
                onClick={() => navigate('/')}
                className="cursor-pointer hover:text-[#326BDF] hover:underline transition-colors duration-200"
              >
                Log In Here
              </span>
            </span>
            <span
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 cursor-pointer hover:text-[#326BDF] transition-colors duration-200"
            >
              ← Back
            </span>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;